import { useMemo, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2, type Select2Option } from '@/components/forms/Select2';
import { symbolsApi } from '@/api/services/onboarding';
import type {
  CreateSymbolDto,
  MissingSymbolDto,
  SymbolDropdownDataDto,
} from '@/api/types/onboarding';
import { renderNormalNumber } from './helpers';

interface Props {
  rows: MissingSymbolDto[];
  dropdowns: SymbolDropdownDataDto;
  onClose: () => void;
  // Called after a successful add so the parent can reload the grid.
  onAdded: () => void;
}

interface RowDraft {
  symbolTypeId: string;
  sbUnderlyingCurrency: string;
  assetClassId: string;
  base: string;
  quote: string;
  quoteAssetClassId: string;
  countryId: string;
}

const SPREAD_BET = 'Spread Bet';

export function MissingSymbolsModal({ rows, dropdowns, onClose, onAdded }: Props) {
  const toast = useToast();
  const [working, setWorking] = useState(rows);

  const initDraft = (r: MissingSymbolDto): RowDraft => ({
    symbolTypeId: String(r.symbolTypeId),
    sbUnderlyingCurrency: '',
    assetClassId: String(r.assetClassId),
    base: r.base,
    quote: r.quote,
    quoteAssetClassId: String(r.quoteAssetClassId),
    countryId: r.countryId != null ? String(r.countryId) : '0',
  });

  const [drafts, setDrafts] = useState<RowDraft[]>(() => rows.map(initDraft));
  // Container (outside the table's scroll region) that select2 renders its dropdowns
  // into, so they aren't clipped inside the modal and stack above it correctly.
  const [dropdownParent, setDropdownParent] = useState<HTMLElement | null>(null);

  const symbolTypeOpts = dropdowns.symbolTypes;
  const assetClassOpts = dropdowns.assetClasses;
  const quoteAssetClassOpts = dropdowns.quoteAssetClasses;
  const countryOpts = useMemo(() => [{ id: 0, name: 'N/A' }, ...dropdowns.countries], [dropdowns.countries]);

  const toOpts = (list: { id: number; name: string }[]): Select2Option[] =>
    list.map((o) => ({ value: String(o.id), label: o.name }));
  const symbolTypeSel = useMemo(() => toOpts(symbolTypeOpts), [symbolTypeOpts]);
  const assetClassSel = useMemo(() => toOpts(assetClassOpts), [assetClassOpts]);
  const quoteAssetClassSel = useMemo(() => toOpts(quoteAssetClassOpts), [quoteAssetClassOpts]);
  const countrySel = useMemo(() => toOpts(countryOpts), [countryOpts]);

  const isSpreadBet = (symbolTypeId: string) =>
    symbolTypeOpts.find((s) => s.id === Number(symbolTypeId))?.name === SPREAD_BET;

  const setDraft = (i: number, patch: Partial<RowDraft>) =>
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const add = async () => {
    // Build CreateSymbolDto[] with validation.
    const payload: CreateSymbolDto[] = [];
    for (let i = 0; i < working.length; i++) {
      const r = working[i];
      const d = drafts[i];
      const spreadBet = isSpreadBet(d.symbolTypeId);
      if (spreadBet && !d.sbUnderlyingCurrency.trim()) {
        toast.error(`SB underlying currency is required for Spread Bet (${r.name})`);
        return;
      }
      const countryNum = Number.parseInt(d.countryId, 10);
      payload.push({
        name: r.name,
        symbolType: Number.parseInt(d.symbolTypeId, 10),
        sbUnderlyingCurrency: spreadBet ? d.sbUnderlyingCurrency : null,
        assetClass: Number.parseInt(d.assetClassId, 10),
        base: d.base,
        quote: d.quote,
        quoteAssetClass: Number.parseInt(d.quoteAssetClassId, 10),
        country: countryNum === 0 || Number.isNaN(countryNum) ? null : countryNum,
        contractSize: r.contractSize != null ? Math.trunc(r.contractSize) : 0,
        digits: r.digits ?? 0,
        dataSource: r.dataSourceId,
      });
    }

    try {
      const result = await symbolsApi.addMissingSymbols(payload);
      onAdded();
      const n = result.addedCount;
      const errCount = result.errors.length;
      const msg =
        `${n} symbol${n !== 1 ? 's' : ''} added successfully.` +
        (errCount > 0 ? ` — ${errCount} symbol${errCount !== 1 ? 's' : ''} to be defined.` : '');
      if (n > 0) toast.success(msg);
      else toast.warning(msg);

      if (errCount > 0) {
        // Re-prompt with only the failed rows (matched by the prefix before ':').
        const failedNames = new Set(result.errors.map((e) => e.split(':')[0].trim()));
        const remaining = working.filter((w) => failedNames.has(w.name));
        if (remaining.length > 0) {
          setWorking(remaining);
          setDrafts(remaining.map(initDraft));
          return;
        }
      }
      onClose();
    } catch {
      toast.error('Failed to add missing symbols.');
    }
  };

  // Index into the parallel `drafts` array by row reference (order is preserved —
  // columns are not sortable).
  const idx = (r: MissingSymbolDto) => working.indexOf(r);

  const columns: DataGridColumn<MissingSymbolDto>[] = [
    { key: 'name', title: 'Instrument', sortable: false, value: (r) => r.name },
    {
      key: 'symbolType',
      title: 'Instrument Type',
      sortable: false,
      cell: (r) => {
        const i = idx(r);
        return (
          <div style={{ minWidth: 150 }}>
            <Select2 value={drafts[i].symbolTypeId} options={symbolTypeSel} placeholder="N/A" allowClear={false} searchable={false} dropdownParent={dropdownParent} onChange={(v) => setDraft(i, { symbolTypeId: v ?? '' })} />
          </div>
        );
      },
    },
    {
      key: 'sb',
      title: 'SB Underlying Currency',
      sortable: false,
      cell: (r) => {
        const i = idx(r);
        return <input className="form-control form-control-sm" value={drafts[i].sbUnderlyingCurrency} disabled={!isSpreadBet(drafts[i].symbolTypeId)} onChange={(e) => setDraft(i, { sbUnderlyingCurrency: e.target.value })} />;
      },
    },
    {
      key: 'assetClass',
      title: 'Asset Class',
      sortable: false,
      cell: (r) => {
        const i = idx(r);
        return (
          <div style={{ minWidth: 150 }}>
            <Select2 value={drafts[i].assetClassId} options={assetClassSel} placeholder="N/A" allowClear={false} searchable={false} dropdownParent={dropdownParent} onChange={(v) => setDraft(i, { assetClassId: v ?? '' })} />
          </div>
        );
      },
    },
    { key: 'base', title: 'Base', sortable: false, cell: (r) => { const i = idx(r); return <input className="form-control form-control-sm" value={drafts[i].base} onChange={(e) => setDraft(i, { base: e.target.value })} />; } },
    { key: 'quote', title: 'Quote', sortable: false, cell: (r) => { const i = idx(r); return <input className="form-control form-control-sm" value={drafts[i].quote} onChange={(e) => setDraft(i, { quote: e.target.value })} />; } },
    {
      key: 'quoteAssetClass',
      title: 'Quote Asset Class',
      sortable: false,
      cell: (r) => {
        const i = idx(r);
        return (
          <div style={{ minWidth: 150 }}>
            <Select2 value={drafts[i].quoteAssetClassId} options={quoteAssetClassSel} placeholder="N/A" allowClear={false} searchable={false} dropdownParent={dropdownParent} onChange={(v) => setDraft(i, { quoteAssetClassId: v ?? '' })} />
          </div>
        );
      },
    },
    {
      key: 'country',
      title: 'Country',
      sortable: false,
      cell: (r) => {
        const i = idx(r);
        return (
          <div style={{ minWidth: 160 }}>
            <Select2 value={drafts[i].countryId} options={countrySel} allowClear={false} dropdownParent={dropdownParent} onChange={(v) => setDraft(i, { countryId: v ?? '0' })} />
          </div>
        );
      },
    },
    { key: 'contractSize', title: 'Contract Size', sortable: false, className: 'text-end', cell: (r) => <input className="form-control form-control-sm text-end" value={renderNormalNumber(r.contractSize)} disabled /> },
    { key: 'connectors', title: 'Connectors', sortable: false, value: (r) => r.dataSourceName },
    { key: 'digits', title: 'Digits', sortable: false, className: 'text-end', cell: (r) => <input className="form-control form-control-sm text-end" value={r.digits ?? ''} disabled /> },
  ];

  return (
    <Modal
      show
      title="Missing Symbols!"
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
          <AsyncButton className="btn btn-primary" icon="fa fa-plus" onClick={add}>Add</AsyncButton>
        </>
      }
    >
      {/* ref container sits ABOVE the table's scroll region so select2 dropdowns
          (dropdownParent) escape the overflow clip. */}
      <div ref={setDropdownParent}>
        <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
          <DataGrid
            columns={columns}
            data={working}
            rowKey={(r) => `${r.name}-${r.dataSourceId}`}
            paginated={false}
            columnVisibilityToggle={false}
            emptyMessage="No missing symbols."
          />
        </div>
      </div>
    </Modal>
  );
}
